import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { CButton, CCard, CCardBody, CCardHeader, CSpinner } from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from 'components/globals/ToastNotification';
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";

const ComplaintList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [toast, setToast] = useState();

    const [complaints, setComplaints] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [isLoading, setIsLoading] = useState(true);  // Loading state
    const [previewComplaint, setPreviewComplaint] = useState(null);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    useEffect(() => {
        // Fetch complaints on component load or when selectedYear changes
        const fetchComplaints = async () => {
            setIsLoading(true);  // Start loading
            try {
                const response = await apiClient.get(`/complaints/?year=${selectedYear}`);
                setComplaints(response.data.complaints);
                setPreviewComplaint((current) => current
                    ? response.data.complaints.find((complaint) => complaint.id === current.id) || null
                    : null);
                setAvailableYears(response.data.available_years);
            } catch (error) {
                setToast({ title: 'Reclamações', message: 'Erro a carregar as reclamções', type: 'error' });
            } finally {
                setIsLoading(false);  // End loading
            }
        };

        fetchComplaints();
    }, [selectedYear, location.pathname]);

    const handleDelete = async (complaintId) => {
        try {
            await apiClient.delete(`/complaints/${complaintId}/`);
            setComplaints((current) => current.filter((complaint) => complaint.id !== complaintId));
            setPreviewComplaint((current) => current?.id === complaintId ? null : current);
        } catch (error) {
            console.error('Error deleting complaint:', error);
        }
    };

    const columns = [
        { name: "Data", selector: (row) => row.created_at, sortable: true, className: 'text-center' },
        { name: "Código", selector: (row) => row.code, sortable: true, className: 'text-center' },
        {
            name: "Descrição",
            selector: (row) => (
                <div
                    dangerouslySetInnerHTML={{ __html: row.description }}
                    onClick={() => setPreviewComplaint(row)}
                    style={{ cursor: "pointer" }}
                />
            ),
            sortable: true,
        },
    ];

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Reclamaçãos</h3>
                    <Link to="/reclamacoes/new">
                        <CButton color="info" className="text-white">Nova Reclamação</CButton>
                    </Link>
                </CCardHeader>
                <CCardBody>
                    {isLoading ? (
                        <div className="text-center">
                            <CSpinner color="primary" />
                        </div>
                    ) : (
                        <DataTableComponent
                            columns={columns}
                            data={complaints}
                            years={availableYears}
                            selectedYear={selectedYear}
                            onYearChange={(newYear) => setSelectedYear(newYear)}
                            onRowClicked={setPreviewComplaint}
                        />
                    )}
                </CCardBody>
            </CCard>

            <RecordDetailOffcanvas
                record={previewComplaint}
                onHide={() => setPreviewComplaint(null)}
                title="Reclamação"
                subtitle={previewComplaint?.code}
                fields={previewComplaint ? [
                    { label: "Data", value: previewComplaint.created_at },
                    { label: "Código", value: previewComplaint.code },
                    {
                        label: "Descrição",
                        value: <div dangerouslySetInnerHTML={{ __html: previewComplaint.description || "-" }} />,
                        plain: false,
                    },
                ] : []}
                headerActions={previewComplaint && <>
                    <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/reclamacoes/${previewComplaint.id}`)}>
                        {previewComplaint.approved == null && (user_id === previewComplaint.received_by || is_director) ? "Editar" : "Ver"}
                    </CButton>
                    {previewComplaint.approved === null && is_director && (
                        <CButton color="danger" size="sm" className="text-white" onClick={() => {
                            if (window.confirm("Tem a certeza que quer eliminar este registo?")) handleDelete(previewComplaint.id);
                        }}>
                            Eliminar
                        </CButton>
                    )}
                </>}
            />
        </>
    );


};

export default ComplaintList;
